import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Box, Stack, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import { useEffect, useMemo } from 'react';
import {
  ChipField,
  minValue,
  required,
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { FINANCIAL_INDEX_SHORT_NAME } from '@Common/constants/financial_indexes';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import {
  computeDaysAfterFiscalDeadline,
  computeLateInterestsAmount,
} from '@Common/types/bo/b2b/campaigns/late_interests';

import { FileInput, ShowSection, TypographyTranslated } from '@sh4res/bo-common/components';
import { ConsultationPeriodSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/ConsultationPeriodSection';
import { MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/matchingContributionCeilings';
import {
  AllocationsFileError,
  parseFile,
  parseFileCompanyAllocator,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/utils';
import { DownloadAllocationSampleOptions } from '@sh4res/bo-common/components/B2bCampaigns/steps/components/DownloadAllocationSampleOptions/DownloadAllocationSampleOptions';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { AmountInput } from '@sh4res/bo-common/fields/AmountInput';
import { useFinancialIndexValue, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { convertCentsInUnits, inputDisabledProps } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCampaignAllocationsStep';

export const AllocationsStep = ({
  isBoExternal,
  isOngoingConsultationEdit = false,
  distributorStepsOverride = false,
}: {
  isBoExternal: boolean;
  isOngoingConsultationEdit?: boolean;
  distributorStepsOverride?: boolean;
}) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const notify = useNotify();
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const resourceBasePath = useResourceBasePath();
  const dataProvider = useDataProvider();

  const [
    amountType,
    campaignType,
    fiscalPeriodStart,
    fiscalPeriodEnd,
    choiceCollectedBy,
    allocationComputedBy,
    contractIds,
    matchingContributionComputedBy,
    matchingContributionRelatedCashSource,
  ] = useWatch({
    name: [
      'amount_type_input',
      'type',
      'fiscal_period_start',
      'fiscal_period_end',
      'choice_collected_by',
      'allocation_computed_by',
      'b2b_contract_ids',
      'matching_contribution_computed_by',
      'matching_contribution_related_cash_source',
    ],
  });

  const endDate = useWatch({ name: 'end_date', defaultValue: record?.end_date });

  const editionDisabled = useMemo(() => {
    if (allocationComputedBy !== 'distributor') {
      return false;
    }
    if (isBoExternal) {
      // Always disabled in external BO when allocations computed by distributor
      return true;
    }
    if (distributorStepsOverride) {
      return false;
    }

    return record?.status === 'campaign_awaiting_customer_confirmation';
  }, [record, isBoExternal, allocationComputedBy, distributorStepsOverride]);

  // Option 3
  const isDistributorOnlyFlow =
    allocationComputedBy === 'distributor' && choiceCollectedBy === 'distributor';

  // In option 3 the allocations and the late payment interests are the distributor's business
  const isConsultationPeriodOnly =
    isDistributorOnlyFlow && isBoExternal && !isOngoingConsultationEdit;

  // In option 3 the client sets the consultation period while the campaign is a draft, so the
  // distributor only reads it back, unless it runs the whole flow itself.
  const consultationPeriodEditionDisabled = useMemo(() => {
    if (!isDistributorOnlyFlow) {
      return editionDisabled;
    }
    if (isBoExternal) {
      return !!record && record.status !== 'campaign_draft';
    }

    return !distributorStepsOverride;
  }, [isDistributorOnlyFlow, isBoExternal, record, distributorStepsOverride, editionDisabled]);

  const isStartDateInFuture = useMemo(
    () => !!record?.start_date && isAfter(record.start_date, new Date()),
    [record?.start_date],
  );

  const daysAfterFiscalDeadline = useMemo<number>(() => {
    const finalEndDate = choiceCollectedBy === 'distributor' ? endDate : new Date();
    if (
      G.isNullable(finalEndDate) ||
      G.isNullable(fiscalPeriodEnd) ||
      G.isNullable(fiscalPeriodStart)
    ) {
      return 0;
    }

    return computeDaysAfterFiscalDeadline({
      endDate: new Date(finalEndDate),
      fiscalPeriodStart: new Date(fiscalPeriodStart),
      fiscalPeriodEnd: new Date(fiscalPeriodEnd),
      campaignType,
      choiceCollectedBy,
    });
  }, [endDate, fiscalPeriodEnd, fiscalPeriodStart, campaignType, choiceCollectedBy]);

  useEffect(() => {
    if (daysAfterFiscalDeadline <= 0 && !isOngoingConsultationEdit) {
      setValue('is_automatic_late_payment_interests', undefined);
      setValue('late_payment_interests_override_amount', undefined);
    }
  }, [daysAfterFiscalDeadline, setValue, isOngoingConsultationEdit]);

  const onFileChange = async (file: File | null) => {
    // A new list voids whatever the last ceiling check reported
    setValue(MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD, []);

    if (!file) {
      setValue('allocations', null);
      return;
    }
    try {
      const data =
        choiceCollectedBy === 'company'
          ? await parseFileCompanyAllocator({
              file,
              amountType,
              campaignType,
              contractIds,
              resourceBasePath,
              dataProvider,
              matchingContributionComputedBy,
              matchingContributionRelatedCashSource,
            })
          : await parseFile({ file, amountType });
      setValue('allocations', data, { shouldDirty: true });
    } catch (err) {
      const allocationsFileError = err as AllocationsFileError | Error;
      // We want to expose the error some way. It's based on FE side validation.
      // eslint-disable-next-line no-console
      console.error(allocationsFileError);
      notify(
        allocationsFileError instanceof AllocationsFileError
          ? allocationsFileError.messageKey
          : 'resources.bo_common.b2b.campaigns.sections.allocations.errors.parsing_file',
        {
          type: 'error',
          messageArgs:
            allocationsFileError instanceof AllocationsFileError
              ? allocationsFileError.messageData
              : undefined,
        },
      );
      setValue('allocations_file', undefined);
    }
  };

  return (
    <Box>
      <ConsultationPeriodSection
        isEditionDisabled={consultationPeriodEditionDisabled && !isOngoingConsultationEdit}
        isStartDateEditionDisabled={
          (consultationPeriodEditionDisabled && !isOngoingConsultationEdit) ||
          // the consultation has already started, so its start date can no longer be changed
          (isOngoingConsultationEdit && !isStartDateInFuture)
        }
      />
      {!isConsultationPeriodOnly &&
        daysAfterFiscalDeadline > 0 &&
        campaignType !== 'unused_vacation_days' && (
          <LatePaymentInterests
            daysAfterFiscalDeadline={daysAfterFiscalDeadline}
            editionDisabled={editionDisabled}
            isOngoingConsultationEdit={isOngoingConsultationEdit}
          />
        )}
      {!isConsultationPeriodOnly && (
        <ShowSection
          leftAligned
          label="resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations"
          noCard={true}>
          <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Warning />
            <span>
              {translate(
                'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_description.part1',
              )}
              <strong>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_description.part2bold',
                )}
              </strong>
              {translate(
                'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_description.part3',
              )}
              <strong>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_description.part4bold',
                )}
              </strong>
              {translate(
                'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_description.part5',
              )}
            </span>
          </Typography>

          <Box>
            <DownloadAllocationSampleOptions />

            {!editionDisabled && (
              <FileInput
                data-testid={`${testId}_AllocationsFileInput`}
                source="allocations_file"
                accept=".xlsx,.xlsm"
                onChange={onFileChange}
                label="resources.bo_common.b2b.campaigns.fields.allocations_file">
                <ChipField data-testid={`${testId}_AllocationsFileTitleField`} source="title" />
              </FileInput>
            )}
          </Box>

          {isOngoingConsultationEdit ? (
            <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Warning />
              <span>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.allocations.allocations_removal_warning',
                )}
              </span>
            </Typography>
          ) : (
            <Typography fontWeight={700}>
              {translate(
                'resources.bo_common.b2b.campaigns.sections.allocations.employee_allocations_manual',
              )}
            </Typography>
          )}
        </ShowSection>
      )}
    </Box>
  );
};

const LatePaymentInterests = ({
  daysAfterFiscalDeadline,
  editionDisabled,
  isOngoingConsultationEdit,
}: {
  daysAfterFiscalDeadline: number;
  editionDisabled: boolean;
  isOngoingConsultationEdit: boolean;
}) => {
  const { setValue, ...form } = useFormContext();
  const tmoPct = useFinancialIndexValue(FINANCIAL_INDEX_SHORT_NAME.TMO);
  const amountInput = form.getValues('amount_input');
  const isAutomaticLatePaymentInterests: 'yes' | 'no' | undefined = useWatch({
    name: 'is_automatic_late_payment_interests',
  });
  const lateInterestsOverrideAmount: number | null | undefined = useWatch({
    name: 'late_payment_interests_override_amount',
  });

  const latePaymentInterests = G.isNotNullable(tmoPct)
    ? computeLateInterestsAmount({
        amount: amountInput,
        daysAfterFiscalDeadline,
        tmoPct,
      })
    : null;

  useEffect(() => {
    if (isAutomaticLatePaymentInterests === 'yes') {
      setValue('late_payment_interests_override_amount', undefined);
    }
  }, [isAutomaticLatePaymentInterests, setValue]);

  const showOverrideAmount =
    isAutomaticLatePaymentInterests === 'no' ||
    (isOngoingConsultationEdit && G.isNotNullable(lateInterestsOverrideAmount));

  return (
    <>
      <TypographyTranslated
        label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.title"
        color="error"
        fontWeight="bold"
      />
      <TypographyTranslated
        label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.subtitle"
        sx={{
          fontSize: '11px',
          color: 'DarkGray',
          fontWeight: '400',
          fontStyle: 'italic',
          marginTop: '-5px',
        }}
      />
      <br />
      <TypographyTranslated
        label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.description"
        variables={{
          days: String(daysAfterFiscalDeadline),
        }}
      />
      <br />
      <TypographyTranslated label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.formula" />
      {G.isNotNullable(tmoPct) && G.isNotNullable(latePaymentInterests) ? (
        <Typography>
          {convertCentsInUnits(latePaymentInterests)} € = {convertCentsInUnits(amountInput)} x{' '}
          {tmoPct} x 1.33 x {daysAfterFiscalDeadline}/365
        </Typography>
      ) : null}
      <br />
      <RadioButtonGroupInput
        data-testid={`${testId}_LatePaymentInterestsInput`}
        source="is_automatic_late_payment_interests"
        label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.question"
        choices={[
          {
            id: 'yes',
            name: 'resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.choices.yes',
          },
          {
            id: 'no',
            name: 'resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.choices.no',
          },
        ]}
        inline={true}
        required={true}
        disabled={editionDisabled}
        labelSx={{
          fontSize: '0.92rem',
          width: '50%',
        }}
      />
      <br />
      <TypographyTranslated label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.distribution" />
      {showOverrideAmount && (
        <>
          <br />
          <Stack direction="row" alignItems="center" gap={2} maxWidth="700px">
            <TypographyTranslated label="resources.bo_common.b2b.campaigns.sections.allocations.late_payment_interests.override_amount" />
            <AmountInput
              data-testid={`${testId}_LatePaymentInterestsOverrideAmountInput`}
              sx={{ maxWidth: '130px' }}
              source="late_payment_interests_override_amount"
              isRequired
              defaultValue={0}
              min={0}
              validate={[required(), minValue(0)]}
              label={false}
              {...inputDisabledProps(editionDisabled)}
            />
            <Box>€</Box>
          </Stack>
        </>
      )}
    </>
  );
};
